import { useCallback } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { notification } from 'antd'
import { api } from './client'
import type { RobotQueryParams } from './robots.api'
import { formatFileName } from '@/utils/download'
import type { AccountQueryParams } from './accounts.api'
import type { RobotEventQueryParams } from './robot-events.api'
import type { UnitQueryParams } from './units.api'
import type { VersionQueryParams } from './versions.api'
import type { PermissionQueryParams } from './permissions.api'
import type { FeedbackSearchParams, ReceptionHistorySearchParams } from '@/types/api'
import type { HardQuestionExportParams } from './feedback-conversation.api'
import type { FloorplanQueryParams } from './floorplans.api'
import type { EventQueryParams } from './events.api'
import type { KnowledgeQueryParams } from './library.api'
import type { CounterQueryParams } from './counters.api'
import type { MailQueryParams } from './mails.api'
import type { MailTemplateQueryParams } from './mail-templates.api'
import type { RobotFeedbackSearchParams } from './feedbacks.api'

// ==========================================
// 1. TYPES & INTERFACES
// ==========================================

export interface StyleConfig {
    bold?: boolean
    backgroundColor?: string
    textColor?: string
    align?: 'left' | 'center' | 'right'
    numberFormat?: string
}

export interface ColumnExportConfig<T = any> {
    header: string
    field?: string
    width?: number
    type?: 'string' | 'number' | 'currency' | 'date'
    align?: 'left' | 'center' | 'right'
    style?: StyleConfig | ((row: T) => StyleConfig)
    statusStyles?: Record<string, StyleConfig | undefined>
    formatter?: (val: any, row: T, index: number) => any
}

// Type mở rộng hỗ trợ exportStyle truyền trực tiếp Object/Callback Function và exportStatusStyles
export type CombinedColumnDef<TData = any, TValue = any> = ColumnDef<TData, TValue> & {
    align?: 'left' | 'center' | 'right'
    exportable?: boolean
    exportLabel?: string
    exportFormatter?: (val: any, record: TData, index: number) => any
    exportType?: 'string' | 'number' | 'currency' | 'date'
    exportStyle?: StyleConfig | ((record: TData) => StyleConfig)
    exportStatusStyles?: Record<string, StyleConfig | undefined>
}

export interface ExportPayload {
    sheetName: string
    columns: {
        header: string
        width?: number
        type?: string
        style?: StyleConfig
        statusStyles?: Record<string, StyleConfig | undefined>
    }[]
    filter?: Record<string, any>
}

export interface DownloadExcelOptions {
    method?: 'GET' | 'POST'
    url: string
    params?: Record<string, any>
    body?: any
    filename?: string
    signal?: AbortSignal
}

export interface RobotExportParams extends RobotQueryParams {
    page?: number
    limit?: number
}

export interface UserExportParams extends AccountQueryParams {
    page?: number
    limit?: number
    status?: number
}

export interface RobotEventExportParams extends RobotEventQueryParams {
    page?: number
    limit?: number
    unitId?: string
    updatedAtFrom?: string
    updatedAtTo?: string
    robotId: string
}

export interface UnitExportParams extends UnitQueryParams {
    page?: number
    size?: number
}

export interface VersionExportParams extends VersionQueryParams {
    page?: number
    size?: number
}

export interface PermissionExportParams extends PermissionQueryParams {
    page?: number
    size?: number
    unitId?: string
}

export interface SurveyExportParams extends FeedbackSearchParams {
    page?: number
    size?: number
    robotName?: string
    robotId?: string
    unitId?: string
}

export interface RobotSurveyExportParams extends RobotFeedbackSearchParams {
    page?: number
    size?: number
}

export interface HistoryExportParams extends ReceptionHistorySearchParams {
    page?: number
    size?: number
}

export interface FloorplanExportParams extends FloorplanQueryParams {
    page?: number
    size?: number
}

export interface KbExportParams extends KnowledgeQueryParams {
    page?: number
    size?: number
    kbId: string
}

export interface EventExportParams extends EventQueryParams {
    page?: number
    size?: number
}
export interface CounterExportParams extends CounterQueryParams {
    page?: number
    limit?: number
}

export interface MailExportParams extends MailQueryParams {
    page?: number
    size?: number
}

export interface MailTemplateExportParams extends MailTemplateQueryParams {
    page?: number
    size?: number
}

interface ExportTableOptions<T> {
    sheetName: string
    columns: CombinedColumnDef<T>[]
    filter?: Record<string, any>
    exportApiFn?: (payload: ExportPayload) => Promise<any>
}

// ==========================================
// 2. HELPER UTILS
// ==========================================

export function parseBadgeClassToStyle(className: string): StyleConfig {
    const bgMatch = className.match(/bg-\[([^\]]+)\]/)
    const textMatch = className.match(/text-\[([^\]]+)\]/)

    return {
        ...(bgMatch && { backgroundColor: bgMatch[1] }),
        ...(textMatch && { textColor: textMatch[1] }),
        bold: true,
    }
}

export function extractExportColumns<TData>(
    columns: CombinedColumnDef<TData>[]
): ColumnExportConfig<TData>[] {
    return columns
        .filter((col) => col.exportable !== false)
        .map((col) => {
            const headerStr =
                col.exportLabel ||
                (typeof col.header === 'string' ? col.header : String(col.id || ''))

            const field = ('accessorKey' in col ? (col.accessorKey as string) : col.id) || ''
            const width = col.size ? Math.max(10, Math.round(col.size / 6)) : 20
            const align = col.align || 'left'

            const isSttColumn = col.id === 'stt' || field === 'stt'
            const defaultFormatter = isSttColumn ? (_: any, __: any, index: number) => index + 1 : undefined

            return {
                header: headerStr.toUpperCase(),
                field,
                width,
                align,
                style:
                    typeof col.exportStyle === 'function'
                        ? col.exportStyle
                        : { align, ...col.exportStyle },
                statusStyles: col.exportStatusStyles,
                type: col.exportType || 'string',
                formatter: col.exportFormatter || defaultFormatter,
            }
        })
}

export function buildExportPayload<T>({
    sheetName,
    columnsConfig,
    columns,
    filter,
}: {
    sheetName: string
    columnsConfig?: ColumnExportConfig<T>[]
    columns?: CombinedColumnDef<T>[]
    filter?: Record<string, any>
}): ExportPayload {
    const configs = columnsConfig || (columns ? extractExportColumns(columns) : [])

    const exportColumns = configs.map((col) => {
        const baseAlign = col.align || 'left'

        let computedStyle: StyleConfig = {}
        if (typeof col.style === 'function') {
            computedStyle = col.style({} as T)
        } else if (typeof col.style === 'object') {
            computedStyle = col.style
        }

        const resolvedStyle: StyleConfig = {
            align: baseAlign,
            ...computedStyle,
        }

        return {
            header: col.header,
            width: col.width || 20,
            type: col.type || 'string',
            style: resolvedStyle,
            ...(col.statusStyles && { statusStyles: col.statusStyles }),
        }
    })

    return {
        sheetName,
        columns: exportColumns,
        filter,
    }
}

export async function downloadExcelFile(options: DownloadExcelOptions): Promise<void> {
    const {
        method = 'POST',
        url,
        params,
        body,
        filename = 'export.xlsx',
        signal,
    } = options

    try {
        let response
        if (method === 'POST') {
            response = await api.post(url, body, {
                params,
                headers: {
                    'Content-Type': 'application/json',
                },
                responseType: 'blob',
                signal,
            })
        } else {
            response = await api.get(url, {
                params,
                responseType: 'blob',
                signal,
            })
        }

        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()

        link.remove()
        window.URL.revokeObjectURL(blobUrl)
    } catch (error: any) {
        if (error?.response?.data instanceof Blob && error.response.data.type === 'application/json') {
            try {
                const text = await error.response.data.text()
                const errJson = JSON.parse(text)
                throw new Error(errJson.responseMessage || errJson.message)
            } catch {
                // Ignore parse error
            }
        }
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi tải file Excel.'
        throw new Error(message)
    }
}

export async function exportTableData<T>({
    sheetName,
    columns,
    filter,
    exportApiFn,
}: ExportTableOptions<T>) {
    try {
        const payload = buildExportPayload({
            sheetName,
            columns,
            filter,
        })

        if (exportApiFn) {
            await exportApiFn(payload)
        }

        notification.success({
            title: 'Xuất file thành công!',
        })
    } catch (error) {
        notification.error({
            title: 'Xuất file thất bại. Có lỗi xảy ra khi xuất file',
        })
    }
}

// ==========================================
// 3. REACT HOOK FOR COMPONENTS
// ==========================================
export function useTableExport<T>(
    sheetName: string,
    columns: CombinedColumnDef<T>[],
    exportApiFn?: (payload: ExportPayload) => Promise<any>
) {
    const handleExport = useCallback(() => {
        void exportTableData({
            sheetName,
            columns,
            exportApiFn,
        })
    }, [sheetName, columns, exportApiFn])

    return { handleExport }
}

// ==========================================
// 4. API SERVICE MODULE
// ==========================================

export async function exportRobots(
    params: RobotExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { robotId, unitId, status, startDate, endDate } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/robot/config/export',
        params: {
            robotId,
            unitId,
            status,
            startDate,
            endDate,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportEventRobot(
    params: RobotEventExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const {
        robotId,
        keySearch,
        status,
        eventType,
        unitId,
        startDateFrom,
        startDateTo,
        updatedAtFrom,
        updatedAtTo,
    } = params

    const jsonBody =
        typeof sheetFormatJson === 'object'
            ? JSON.stringify(sheetFormatJson)
            : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: `/v1/robot/config/event/${robotId}/export`,
        params: {
            keySearch,
            status,
            eventType,
            unitId,
            startDateFrom,
            startDateTo,
            updatedAtFrom,
            updatedAtTo,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportUser(
    params: UserExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { username, unitId, email, fullName, status, fromDate, toDate } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/user-management/export',
        params: {
            username,
            unitId,
            email,
            fullName,
            status,
            fromDate,
            toDate,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportUnit(
    params: UnitExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { keySearch, status, fromDate, toDate } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/unit-management/export',
        params: {
            keySearch,
            status,
            fromDate,
            toDate,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportVersion(
    params: VersionExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { versionName, status, fromDate, toDate } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/version-management/export',
        params: {
            versionName,
            status,
            fromDate,
            toDate,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportPermission(
    params: PermissionExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { keySearch, unitId, status, fromDate, toDate } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/permission-management/group/export',
        params: {
            keySearch,
            unitId,
            status,
            fromDate,
            toDate,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportSurvey(
    params: SurveyExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const {
        documentId,
        surveyType,
        surveyTypeName,
        satisfactionScore,
        fromDate,
        toDate,
        robotName,
        robotId,
        unitId,
        problemType,
    } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/survey/export',
        params: {
            documentId,
            surveyType,
            surveyTypeName,
            satisfactionScore,
            fromDate,
            toDate,
            robotName,
            robotId,
            unitId,
            problemType,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportHardQuestions(
    params: HardQuestionExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { fromDate, toDate, unitId } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/survey/hard-question/export',
        params: {
            fromDate,
            toDate,
            unitId,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportRobotSurvey(
    params: RobotSurveyExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal,
): Promise<void> {
    const {
        satisfactionScore,
        fromDate,
        toDate,
        robotName,
        robotId,
        unitId,
    } = params

    const jsonBody =
        typeof sheetFormatJson === 'object'
            ? JSON.stringify(sheetFormatJson)
            : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/survey/robot/export',
        params: {
            satisfactionScore,
            fromDate,
            toDate,
            robotName,
            robotId,
            unitId,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportHistory(
    params: HistoryExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { robotId, customerName, title, fromDate, toDate, robotName, unitId } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/session-info/export',
        params: {
            robotId,
            customerName,
            title,
            fromDate,
            toDate,
            robotName,
            unitId,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportFloorPlans(
    params: FloorplanExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { unitId, keySearch, status, fromDate, toDate } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/diagram-management/export',
        params: {
            unitId,
            keySearch,
            status,
            fromDate,
            toDate,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportKb(
    params: KbExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { fileName, fromDate, toDate, kbId } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: `/v1/robot/kb-management/export/${kbId}`,
        params: {
            fileName,
            fromDate,
            toDate,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportEvent(
    params: EventExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { unitId, keySearch, status, startDateFrom, startDateTo, updatedAtFrom, updatedAtTo } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/event-management/export',
        params: {
            unitId,
            keySearch,
            status,
            startDateFrom,
            startDateTo,
            updatedAtFrom,
            updatedAtTo,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportCounters(
    params: CounterExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { deskNumber, name, unitId, diagramId, status, fromDate, toDate } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/reception-desk-management/export',
        params: {
            deskNumber,
            name,
            unitId: unitId?.trim() || undefined,
            diagramId,
            status,
            fromDate,
            toDate,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}


export async function exportMails(
    params: MailExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { mail, name, unitId, protocol, status, fromDate, toDate } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/robot/mail-server-management/export',
        params: {
            mail,
            name,
            unitId: unitId?.trim() || undefined,
            protocol,
            status,
            fromDate,
            toDate,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}

export async function exportMailTemplates(
    params: MailTemplateExportParams,
    sheetFormatJson: string | object,
    filename?: string,
    signal?: AbortSignal
): Promise<void> {
    const { name, title, functionId, unitId, status, fromDate, toDate } = params

    const jsonBody = typeof sheetFormatJson === 'object'
        ? JSON.stringify(sheetFormatJson)
        : sheetFormatJson

    await downloadExcelFile({
        method: 'POST',
        url: '/v1/robot/mail-template-management/export',
        params: {
            name,
            title,
            functionId,
            unitId: unitId?.trim() || undefined,
            status,
            fromDate,
            toDate,
        },
        body: jsonBody,
        filename: formatFileName(filename, 'download'),
        signal,
    })
}